import httpClient from "./httpClient";

const prefix = "/incident-recovery";

export interface PartnerSearchResult {
  partnerId: number;
  fullName: string;
  branchId: number | null;
  branchName: string | null;
  status: string | null;
}

export interface RecoveryAction {
  operation: "REACTIVATE_SCHEDULED_UNSUBSCRIBE";
  label: string;
  requestId: number | null;
  programedId: number | null;
  effectiveDate: string | null;
  currentStatus: string | null;
  targetStatus: "Activo";
  scheduledUnsubscribePreserved: boolean;
}

export interface PartnerDiagnosis {
  partnerId: number;
  fullName: string;
  branchId: number | null;
  branchName: string | null;
  status: string | null;
  reasonUnsubscribe: string | null;
  temporarilySuspend: boolean;
  notifiedInasistance: boolean;
  requests: Array<{
    id: number;
    temporalUnsubscribe: boolean;
    isApproved: boolean;
    isFinished: boolean;
    banDate: string | null;
    reasonUnsubscribe: string | null;
  }>;
  scheduledUnsubscribes: Array<{
    id: number;
    partnerId: number | null;
    linked: boolean;
    completed: boolean;
    banDate: string | null;
    reasonUnsubscribe: string | null;
  }>;
  effectiveDate: string | null;
  authorizationStatus: string | null;
  recentHistory: Array<{
    id: number;
    action: string;
    reason: string | null;
    notes: string | null;
    createdAt: string;
    performedById: number | null;
    performedByName: string | null;
  }>;
  inconsistencies: string[];
  warnings: string[];
  alreadyApplied: boolean;
  availableActions: RecoveryAction[];
}

export interface RecoveryEvaluation {
  partnerId: number;
  eligible: boolean;
  alreadyApplied: boolean;
  operation: "REACTIVATE_SCHEDULED_UNSUBSCRIBE";
  currentStatus: string | null;
  targetStatus: "Activo";
  scheduledUnsubscribePreserved: boolean;
  requestId: number | null;
  programedId: number | null;
  effectiveDate: string | null;
  warnings: string[];
  blockers: string[];
}

export interface RecoveryResult {
  applied: boolean;
  alreadyApplied: boolean;
  partnerId: number;
  operation: string;
  previousStatus: string;
  status: string;
  requestId: number | null;
  programedId: number | null;
  effectiveDate: string | null;
  scheduledUnsubscribePreserved: boolean;
  performedById: number;
  reason: string;
}

export interface RecoveryHistoryPage {
  page: number;
  limit: number;
  total: number;
  results: Array<{
    id: number;
    createdAt: string;
    partnerId: number | null;
    partnerName: string | null;
    operation: string | null;
    previousStatus: string | null;
    status: string | null;
    result: string | null;
    requestId: number | null;
    programedId: number | null;
    effectiveDate: string | null;
    performedById: number | null;
    performedByName: string | null;
    reason: string | null;
  }>;
}

export const searchIncidentPartners = async (q: string, branchId?: number) => {
  const params = new URLSearchParams({ q });
  if (branchId) params.set("branchId", String(branchId));
  return (await httpClient.get(`${prefix}/partners/search?${params}`))
    .data as PartnerSearchResult[];
};

export const getPartnerDiagnosis = async (partnerId: number) => {
  return (await httpClient.get(`${prefix}/partners/${partnerId}/diagnosis`))
    .data as PartnerDiagnosis;
};

export const evaluatePartnerRecovery = async (partnerId: number) => {
  return (await httpClient.post(`${prefix}/partners/${partnerId}/evaluate`))
    .data as RecoveryEvaluation;
};

export const recoverPartner = async (
  partnerId: number,
  reason: string,
) => {
  return (
    await httpClient.post(`${prefix}/partners/${partnerId}/recover`, {
      operation: "REACTIVATE_SCHEDULED_UNSUBSCRIBE",
      reason,
      confirmed: true,
    })
  ).data as RecoveryResult;
};

export const listIncidentRecoveries = async (page = 1, limit = 10) => {
  return (
    await httpClient.get(`${prefix}/recoveries?page=${page}&limit=${limit}`)
  ).data as RecoveryHistoryPage;
};
